import { CompositionEventsEnum } from "../CompositionEvents";
import { CompositionService } from "./CompositionService";
import type { ResolveAssetUrlFn } from "./CompositionSessionService";
import { Input, UrlSource, ALL_FORMATS } from "mediabunny";
import type { MediaSourceRef } from "../../engine/sources/media-source";
import { VideoClip } from "../../engine/clips/video/video";
import { AudioClip } from "../../engine/clips";
import { ImageClip } from "../../engine/clips/image/image";
import { ScriptLayerClip } from "../../engine/clips/script/script";
import type { ScriptLayerInputConfig } from "../../engine/clips/script/script.interfaces";
import { Timestamp } from "../../engine/models";
import { EngineInternalEventsEnum } from "../../engine/EngineInternalEvents";
import { CompositionStateStore } from "../store/CompositionStateStore";

type ClipSnapshot = {
    id: string;
    type: string;
    layerId?: string;
    assetId?: string | null;
    thumbnailId?: string | null;
    name?: string;
    inSec?: number;
    outSec?: number;
    offsetSec?: number;
    props?: { gain?: number; playbackRate?: number; alpha?: number };
    visual?: {
        x?: number;
        y?: number;
        width?: number;
        height?: number;
        rotation?: number;
        alpha?: number;
        scaleX?: number;
        scaleY?: number;
        crop?: { x: number; y: number; width: number; height: number };
    };
    // ScriptLayerClip-specific
    scriptCuts?: any[];
    gaps?: any[];
    gapInsertions?: any[];
    splits?: any[];
    speakerOverrides?: any[];
    speakerMuteRanges?: any[];
    transcriptHiddenRanges?: any[];
    wordCorrections?: any[];
    transcriptParagraphBreaks?: any[];
    transcriptMarkers?: any[];
    sceneProperties?: any[];
    multicamCameraSetup?: any | null;
    sources?: Array<{
        assetId: string;
        name?: string;
        color?: string;
        gain: number;
        playbackRate: number;
        offsetSec: number;
        range?: { inSec: number; outSec: number };
        segments?: Array<{
            id?: string;
            offsetSec: number;
            playbackRate?: number;
            range: { inSec: number; outSec: number };
        }>;
        visual?: {
            x: number;
            y: number;
            width: number;
            height: number;
            alpha: number;
            crop?: { x: number; y: number; width: number; height: number };
        };
        layerIndex?: number;
    }>;
};

type UndoEntry =
    | { kind: "clip:update"; clipId: string; before: ClipSnapshot; after: ClipSnapshot; at: number }
    | { kind: "clip:add"; clip: ClipSnapshot; at: number }
    | { kind: "clip:remove"; clip: ClipSnapshot; at: number }
    | { kind: "script:edits"; clipId: string; before: Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">; after: Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">; at: number }
    | { kind: "canvas:resize"; before: { width: number; height: number }; after: { width: number; height: number }; at: number }
    | { kind: "canvas:bg"; before: string; after: string; at: number };

const shallowEqualJson = (a: any, b: any) => {
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return false;
    }
};

/**
 * In-memory undo/redo for a single editor session.
 * Does not persist across reloads.
 */
export class UndoManager {
    private static initialized = false;
    private static applying = false;
    private static resolveAssetUrl: ResolveAssetUrlFn | null = null;

    private static undoStack: UndoEntry[] = [];
    private static redoStack: UndoEntry[] = [];

    private static listeners = new Set<() => void>();

    private static clipShadow = new Map<string, ClipSnapshot>();
    private static scriptShadow = new Map<
        string,
        Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">
    >();

    private static canvasShadow: { width: number; height: number; background: string } = {
        width: 1920,
        height: 1080,
        background: "#000000",
    };

    static get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    static get canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    static init(args?: { resolveAssetUrl?: ResolveAssetUrlFn }): void {
        if (args?.resolveAssetUrl) this.resolveAssetUrl = args.resolveAssetUrl;
        if (!CompositionService.isInitialized()) return;

        // Reset stacks per composition session.
        this.undoStack = [];
        this.redoStack = [];
        this.clipShadow.clear();
        this.scriptShadow.clear();

        // Seed shadow state from current engine composition.
        const comp: any = CompositionService.composition;
        this.canvasShadow = {
            width: Number(comp?.settings?.width ?? 1920),
            height: Number(comp?.settings?.height ?? 1080),
            background: String(comp?.settings?.background ?? "#000000"),
        };
        for (const tr of comp?.tracks ?? []) {
            for (const clip of tr?.clips ?? []) {
                const snap = this.snapshotClip(clip);
                this.clipShadow.set(snap.id, snap);
                if (snap.type === "script") {
                    this.scriptShadow.set(snap.id, {
                        scriptCuts: snap.scriptCuts ?? [],
                        gaps: snap.gaps ?? [],
                        gapInsertions: snap.gapInsertions ?? [],
                        splits: snap.splits ?? [],
                        speakerOverrides: snap.speakerOverrides ?? [],
                        speakerMuteRanges: snap.speakerMuteRanges ?? [],
                        transcriptHiddenRanges: snap.transcriptHiddenRanges ?? [],
                        wordCorrections: snap.wordCorrections ?? [],
                        transcriptParagraphBreaks: snap.transcriptParagraphBreaks ?? [],
                        transcriptMarkers: snap.transcriptMarkers ?? [],
                    });
                }
            }
        }

        // Bind listeners.
        if (this.initialized) this.teardown();
        comp.on(CompositionEventsEnum.CLIP_ADDED, this.handleClipAdded);
        comp.on(CompositionEventsEnum.CLIP_REMOVED, this.handleClipRemoved);
        comp.on(CompositionEventsEnum.CLIP_UPDATED, this.handleClipUpdated);
        comp.on(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, this.handleScriptLayerUpdated);
        comp.on(CompositionEventsEnum.CANVAS_RESIZED, this.handleCanvasResized);
        comp.on(CompositionEventsEnum.CANVAS_BACKGROUND_CHANGED, this.handleCanvasBackgroundChanged);
        this.initialized = true;
        this.emitChange();
    }

    static teardown(): void {
        if (!this.initialized) return;
        try {
            const comp: any = CompositionService.composition;
            comp.off?.(CompositionEventsEnum.CLIP_ADDED, this.handleClipAdded);
            comp.off?.(CompositionEventsEnum.CLIP_REMOVED, this.handleClipRemoved);
            comp.off?.(CompositionEventsEnum.CLIP_UPDATED, this.handleClipUpdated);
            comp.off?.(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, this.handleScriptLayerUpdated);
            comp.off?.(CompositionEventsEnum.CANVAS_RESIZED, this.handleCanvasResized);
            comp.off?.(CompositionEventsEnum.CANVAS_BACKGROUND_CHANGED, this.handleCanvasBackgroundChanged);
        } catch {}
        this.initialized = false;
        this.undoStack = [];
        this.redoStack = [];
        this.clipShadow.clear();
        this.scriptShadow.clear();
        this.emitChange();
    }

    static async undo(): Promise<void> {
        const entry = this.undoStack.pop();
        if (!entry) return;
        this.applying = true;
        try {
            await this.applyInverse(entry);
            this.redoStack.push(entry);
        } finally {
            this.applying = false;
        }
        this.emitChange();
    }

    static async redo(): Promise<void> {
        const entry = this.redoStack.pop();
        if (!entry) return;
        this.applying = true;
        try {
            await this.applyForward(entry);
            this.undoStack.push(entry);
        } finally {
            this.applying = false;
        }
        this.emitChange();
    }

    static subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private static emitChange(): void {
        for (const fn of this.listeners) {
            try { fn(); } catch {}
        }
    }

    private static record(entry: UndoEntry): void {
        // New edits clear redo history.
        this.redoStack = [];

        // Coalesce rapid repeated updates into a single undo step.
        const now = entry.at;
        const last = this.undoStack[this.undoStack.length - 1];
        const COALESCE_MS = 400;
        if (entry.kind === "clip:update" && last?.kind === "clip:update") {
            if (last.clipId === entry.clipId && now - last.at < COALESCE_MS) {
                (last as any).after = entry.after;
                (last as any).at = now;
                this.emitChange();
                return;
            }
        }
        if (entry.kind === "canvas:resize" && last?.kind === "canvas:resize") {
            if (now - last.at < COALESCE_MS) {
                (last as any).after = entry.after;
                (last as any).at = now;
                this.emitChange();
                return;
            }
        }
        if (entry.kind === "canvas:bg" && last?.kind === "canvas:bg") {
            if (now - last.at < COALESCE_MS) {
                (last as any).after = entry.after;
                (last as any).at = now;
                this.emitChange();
                return;
            }
        }
        if (entry.kind === "script:edits" && last?.kind === "script:edits") {
            if (last.clipId === entry.clipId && now - last.at < COALESCE_MS) {
                (last as any).after = entry.after;
                (last as any).at = now;
                this.emitChange();
                return;
            }
        }

        this.undoStack.push(entry);
        this.emitChange();
    }

    private static handleClipAdded = (event: any) => {
        if (this.applying) return;
        const clip = event?.detail?.clip;
        if (!clip) return;
        const snap = this.snapshotClip(clip);
        this.clipShadow.set(snap.id, snap);
        if (snap.type === "script") {
            this.scriptShadow.set(snap.id, {
                scriptCuts: snap.scriptCuts ?? [],
                gaps: snap.gaps ?? [],
                gapInsertions: snap.gapInsertions ?? [],
                splits: snap.splits ?? [],
                speakerOverrides: snap.speakerOverrides ?? [],
                speakerMuteRanges: snap.speakerMuteRanges ?? [],
                transcriptHiddenRanges: snap.transcriptHiddenRanges ?? [],
                wordCorrections: snap.wordCorrections ?? [],
                transcriptParagraphBreaks: snap.transcriptParagraphBreaks ?? [],
                transcriptMarkers: snap.transcriptMarkers ?? [],
            });
        }
        this.record({ kind: "clip:add", clip: snap, at: Date.now() });
    };

    private static handleClipRemoved = (event: any) => {
        if (this.applying) return;
        const clipId = String(event?.detail?.clipId ?? "");
        if (!clipId) return;
        const prev = this.clipShadow.get(clipId);
        if (!prev) return;
        this.clipShadow.delete(clipId);
        this.scriptShadow.delete(clipId);
        this.record({ kind: "clip:remove", clip: prev, at: Date.now() });
    };

    private static handleClipUpdated = (event: any) => {
        if (this.applying) return;
        if (event?.detail?.transient) return;
        const clip = event?.detail?.clip;
        if (!clip?.id) return;
        const next = this.snapshotClip(clip);
        const prev = this.clipShadow.get(next.id);
        this.clipShadow.set(next.id, next);
        if (!prev) return;
        if (shallowEqualJson(prev, next)) return;
        this.record({ kind: "clip:update", clipId: next.id, before: prev, after: next, at: Date.now() });
    };

    private static handleScriptLayerUpdated = (event: any) => {
        if (this.applying) return;
        const clipId = String(event?.detail?.script?.clipId ?? "");
        if (!clipId) return;
        const clip: any = CompositionService.getClipById(clipId);
        if (!clip || String(clip.type) !== "script") return;

        const next = {
            scriptCuts: Array.isArray(clip.scriptCuts) ? [...clip.scriptCuts] : [],
            gaps: Array.isArray(clip.gaps) ? [...clip.gaps] : [],
            gapInsertions: Array.isArray(clip.gapInsertions) ? [...clip.gapInsertions] : [],
            splits: Array.isArray(clip.splits) ? [...clip.splits] : [],
            speakerOverrides: Array.isArray(clip.speakerOverrides) ? [...clip.speakerOverrides] : [],
            speakerMuteRanges: Array.isArray((clip as any).speakerMuteRanges) ? [...(clip as any).speakerMuteRanges] : [],
            transcriptHiddenRanges: Array.isArray((clip as any).transcriptHiddenRanges) ? [...(clip as any).transcriptHiddenRanges] : [],
            wordCorrections: Array.isArray((clip as any).wordCorrections) ? [...(clip as any).wordCorrections] : [],
            transcriptParagraphBreaks: Array.isArray((clip as any).transcriptParagraphBreaks) ? [...(clip as any).transcriptParagraphBreaks] : [],
            transcriptMarkers: Array.isArray((clip as any).transcriptMarkers) ? [...(clip as any).transcriptMarkers] : [],
        };
        const prev = this.scriptShadow.get(clipId);
        this.scriptShadow.set(clipId, next);
        // Keep the clip shadow in sync too.
        try {
            const cs = this.clipShadow.get(clipId);
            if (cs) {
                this.clipShadow.set(clipId, { ...cs, ...next });
            }
        } catch {}

        if (!prev) return;
        if (shallowEqualJson(prev, next)) return;
        this.record({ kind: "script:edits", clipId, before: prev, after: next, at: Date.now() });
    };

    private static handleCanvasResized = (event: any) => {
        if (this.applying) return;
        const w = Number(event?.detail?.width ?? 0);
        const h = Number(event?.detail?.height ?? 0);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return;
        const prev = { width: this.canvasShadow.width, height: this.canvasShadow.height };
        const next = { width: w, height: h };
        if (prev.width === next.width && prev.height === next.height) return;
        this.canvasShadow.width = w;
        this.canvasShadow.height = h;
        this.record({ kind: "canvas:resize", before: prev, after: next, at: Date.now() });
    };

    private static handleCanvasBackgroundChanged = (event: any) => {
        if (this.applying) return;
        const color = String(event?.detail?.color ?? "");
        if (!color) return;
        const prev = this.canvasShadow.background;
        if (prev === color) return;
        this.canvasShadow.background = color;
        this.record({ kind: "canvas:bg", before: prev, after: color, at: Date.now() });
    };

    private static snapshotClip(clip: any): ClipSnapshot {
        const id = String(clip?.id ?? "");
        const type = String(clip?.type ?? "unknown");
        const layerId = String(clip?.track?.id ?? "");

        const snap: ClipSnapshot = {
            id,
            type,
            layerId: layerId || undefined,
            name: String(clip?.name ?? ""),
        };

        // Resolve asset mapping from store (best-effort).
        try {
            snap.assetId = CompositionStateStore.clipIdToAssetId[id] ?? null;
        } catch {
            snap.assetId = null;
        }
        try {
            snap.thumbnailId = CompositionStateStore.clipIdToThumbnailId[id] ?? null;
        } catch {
            snap.thumbnailId = null;
        }

        // Timing (MediaClip-style).
        try {
            if ("range" in clip && "offset" in clip) {
                snap.inSec = Number(clip.range?.[0]?.seconds ?? 0);
                snap.outSec = Number(clip.range?.[1]?.seconds ?? snap.inSec ?? 0);
                snap.offsetSec = Number(clip.offset?.seconds ?? 0);
            }
        } catch {}

        // Common props.
        snap.props = {
            gain: Number.isFinite(clip?.gain) ? Number(clip.gain) : 1,
            playbackRate: Number.isFinite(clip?.playbackRate) ? Number(clip.playbackRate) : 1,
            alpha: Number.isFinite(clip?.alpha) ? Number(clip.alpha) : undefined,
        };

        // Visual props.
        try {
            const pos = clip?.position;
            const x = typeof pos?.x === "number" ? pos.x : typeof clip?.x === "number" ? clip.x : undefined;
            const y = typeof pos?.y === "number" ? pos.y : typeof clip?.y === "number" ? clip.y : undefined;
            const scale = clip?.scale;
            const scaleX = typeof scale?.x === "number" ? scale.x : undefined;
            const scaleY = typeof scale?.y === "number" ? scale.y : undefined;

            const visual: ClipSnapshot["visual"] = {
                x,
                y,
                width: typeof clip?.width === "number" ? clip.width : undefined,
                height: typeof clip?.height === "number" ? clip.height : undefined,
                rotation: typeof clip?.rotation === "number" ? clip.rotation : undefined,
                alpha: typeof clip?.alpha === "number" ? clip.alpha : undefined,
                scaleX,
                scaleY,
            };

            if (typeof clip?.getCrop === "function") {
                const c = clip.getCrop();
                if (c) {
                    visual.crop = {
                        x: Number(c.x ?? 0),
                        y: Number(c.y ?? 0),
                        width: Number(c.width ?? 1),
                        height: Number(c.height ?? 1),
                    };
                }
            }

            // Only attach if there is anything meaningful.
            if (!shallowEqualJson(visual, {})) snap.visual = visual;
        } catch {}

        // Script-specific fields.
        if (type === "script" && clip instanceof ScriptLayerClip) {
            try {
                snap.scriptCuts = Array.isArray((clip as any).scriptCuts) ? [...(clip as any).scriptCuts] : [];
                snap.gaps = Array.isArray((clip as any).gaps) ? [...(clip as any).gaps] : [];
                snap.gapInsertions = Array.isArray((clip as any).gapInsertions) ? [...(clip as any).gapInsertions] : [];
                snap.splits = Array.isArray((clip as any).splits) ? [...(clip as any).splits] : [];
                snap.speakerOverrides = Array.isArray((clip as any).speakerOverrides) ? [...(clip as any).speakerOverrides] : [];
                snap.speakerMuteRanges = Array.isArray((clip as any).speakerMuteRanges) ? [...(clip as any).speakerMuteRanges] : [];
                snap.transcriptHiddenRanges = Array.isArray((clip as any).transcriptHiddenRanges) ? [...(clip as any).transcriptHiddenRanges] : [];
                snap.wordCorrections = Array.isArray((clip as any).wordCorrections) ? [...(clip as any).wordCorrections] : [];
                snap.transcriptParagraphBreaks = Array.isArray((clip as any).transcriptParagraphBreaks)
                    ? [...(clip as any).transcriptParagraphBreaks]
                    : [];
                snap.transcriptMarkers = Array.isArray((clip as any).transcriptMarkers) ? [...(clip as any).transcriptMarkers] : [];
            } catch {}
            try {
                const setup = (clip as any).multicamCameraSetup;
                snap.multicamCameraSetup = setup ? JSON.parse(JSON.stringify(setup)) : null;
            } catch {}
            try {
                const map: Map<number, any> | undefined = (clip as any)?.sceneProperties;
                if (map && typeof (map as any).entries === "function") {
                    const out: any[] = [];
                    for (const [sceneIndexRaw, propsRaw] of (map as any).entries()) {
                        const sceneIndex = Number(sceneIndexRaw);
                        if (!Number.isFinite(sceneIndex) || sceneIndex < 0) continue;

                        const props = propsRaw ?? {};
                        const rec: any = { sceneIndex };

                        if (Number.isFinite((props as any)?.gain)) rec.gain = Number((props as any).gain);
                        if (Number.isFinite((props as any)?.playbackRate)) rec.playbackRate = Number((props as any).playbackRate);
                        if (typeof (props as any)?.backgroundColor === "string" && String((props as any).backgroundColor).trim()) {
                            rec.backgroundColor = String((props as any).backgroundColor).trim();
                        }

                        // Per-source audio gain multipliers
                        try {
                            const audioSourceGains = (props as any)?.audioSourceGains;
                            if (audioSourceGains && typeof audioSourceGains?.entries === "function") {
                                const arr: any[] = [];
                                for (const [sourceIndexRaw, gainRaw] of audioSourceGains.entries()) {
                                    const sourceIndex = Number(sourceIndexRaw);
                                    const gain = Number(gainRaw);
                                    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                    if (!Number.isFinite(gain)) continue;
                                    arr.push({ sourceIndex, gain });
                                }
                                arr.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                                if (arr.length > 0) rec.audioSourceGains = arr;
                            }
                        } catch {}

                        if (typeof (props as any)?.layout === "string") rec.layout = String((props as any).layout);
                        try {
                            const byAspect = (props as any)?.layoutByAspect;
                            if (byAspect && typeof byAspect === "object") {
                                const cleaned: any = {};
                                for (const key of ["horizontal", "vertical", "square"]) {
                                    if (!Object.prototype.hasOwnProperty.call(byAspect, key)) continue;
                                    const v = (byAspect as any)[key];
                                    if (v === null) cleaned[key] = null;
                                    else if (typeof v === "string" && v) cleaned[key] = v;
                                }
                                if (Object.keys(cleaned).length > 0) rec.layoutByAspect = cleaned;
                            }
                        } catch {}

                        try {
                            if ((props as any)?.inTransition) rec.inTransition = JSON.parse(JSON.stringify((props as any).inTransition));
                            if ((props as any)?.outTransition) rec.outTransition = JSON.parse(JSON.stringify((props as any).outTransition));
                        } catch {}

                        // visualOverrides (legacy)
                        try {
                            const visualOverrides = (props as any)?.visualOverrides;
                            if (visualOverrides && typeof visualOverrides?.entries === "function") {
                                const vis: any[] = [];
                                for (const [sourceIndexRaw, vRaw] of visualOverrides.entries()) {
                                    const sourceIndex = Number(sourceIndexRaw);
                                    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                    const v = vRaw ?? {};
                                    const vrec: any = { sourceIndex };
                                    if (Number.isFinite(v.alpha)) vrec.alpha = Number(v.alpha);
                                    if (Number.isFinite(v.x)) vrec.x = Number(v.x);
                                    if (Number.isFinite(v.y)) vrec.y = Number(v.y);
                                    if (Number.isFinite(v.width)) vrec.width = Number(v.width);
                                    if (Number.isFinite(v.height)) vrec.height = Number(v.height);
                                    try {
                                        const crop = (v as any)?.crop;
                                        if (crop && typeof crop === "object") {
                                            const x = Number((crop as any).x);
                                            const y = Number((crop as any).y);
                                            const width = Number((crop as any).width);
                                            const height = Number((crop as any).height);
                                            if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                                vrec.crop = { x, y, width, height };
                                            }
                                        }
                                    } catch {}
                                    if (typeof (v as any)?.maskShape === "string") vrec.maskShape = String((v as any).maskShape);
                                    if (typeof (v as any)?.rotation === "number" && Number.isFinite((v as any).rotation)) {
                                        vrec.rotation = Number((v as any).rotation);
                                    }
                                    if (Object.keys(vrec).length > 1) vis.push(vrec);
                                }
                                vis.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                                if (vis.length > 0) rec.visualOverrides = vis;
                            }
                        } catch {}

                        // visualOverridesByAspect
                        try {
                            const byAspect = (props as any)?.visualOverridesByAspect;
                            if (byAspect && typeof byAspect === "object") {
                                const outByAspect: any = {};
                                for (const [presetKey, mapRaw] of Object.entries(byAspect as any)) {
                                    const m: any = mapRaw as any;
                                    if (!m || typeof m?.entries !== "function") continue;
                                    const arr: any[] = [];
                                    for (const [sourceIndexRaw, vRaw] of m.entries()) {
                                        const sourceIndex = Number(sourceIndexRaw);
                                        if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                        const v = vRaw ?? {};
                                        const vrec: any = { sourceIndex };
                                        if (Number.isFinite(v.alpha)) vrec.alpha = Number(v.alpha);
                                        if (Number.isFinite(v.x)) vrec.x = Number(v.x);
                                        if (Number.isFinite(v.y)) vrec.y = Number(v.y);
                                        if (Number.isFinite(v.width)) vrec.width = Number(v.width);
                                        if (Number.isFinite(v.height)) vrec.height = Number(v.height);
                                        try {
                                            const crop = (v as any)?.crop;
                                            if (crop && typeof crop === "object") {
                                                const x = Number((crop as any).x);
                                                const y = Number((crop as any).y);
                                                const width = Number((crop as any).width);
                                                const height = Number((crop as any).height);
                                                if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                                    vrec.crop = { x, y, width, height };
                                                }
                                            }
                                        } catch {}
                                        if (typeof (v as any)?.maskShape === "string") vrec.maskShape = String((v as any).maskShape);
                                        if (typeof (v as any)?.rotation === "number" && Number.isFinite((v as any).rotation)) {
                                            vrec.rotation = Number((v as any).rotation);
                                        }
                                        if (Object.keys(vrec).length > 1) arr.push(vrec);
                                    }
                                    arr.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                                    if (arr.length > 0) outByAspect[String(presetKey)] = arr;
                                }
                                if (Object.keys(outByAspect).length > 0) rec.visualOverridesByAspect = outByAspect;
                            }
                        } catch {}

                        if (Object.keys(rec).length > 1) out.push(rec);
                    }

                    out.sort((a, b) => (a.sceneIndex ?? 0) - (b.sceneIndex ?? 0));
                    snap.sceneProperties = out;
                }
            } catch {}
            try {
                const cfgs = clip.getInputConfigs();
                snap.sources = cfgs.map((c, idx) => ({
                    assetId: String(c.assetId ?? ""),
                    name: c.name ?? "",
                    color: c.color ?? undefined,
                    gain: Number.isFinite(c.gain) ? Number(c.gain) : 1.0,
                    playbackRate: Number.isFinite(c.playbackRate) ? Number(c.playbackRate) : 1.0,
                    offsetSec: Number.isFinite(c.offsetSec) ? Number(c.offsetSec) : 0,
                    range: c.range ? { inSec: Number(c.range.inSec ?? 0), outSec: Number(c.range.outSec ?? 0) } : undefined,
                    segments:
                        Array.isArray((c as any).segments) && (c as any).segments.length > 0
                            ? ((c as any).segments as any[])
                                  .map((s: any) => {
                                      const offsetSec = Number(s?.offsetSec ?? NaN);
                                      const rin = Number(s?.range?.inSec ?? NaN);
                                      const rout = Number(s?.range?.outSec ?? NaN);
                                      if (!Number.isFinite(offsetSec) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
                                      const rec: any = {
                                          id: s?.id ? String(s.id) : undefined,
                                          offsetSec,
                                          range: { inSec: rin, outSec: rout },
                                      };
                                      const pr = s?.playbackRate;
                                      if (Number.isFinite(pr) && Number(pr) > 0) rec.playbackRate = Number(pr);
                                      return rec;
                                  })
                                  .filter(Boolean)
                            : undefined,
                    visual: c.visual
                        ? {
                              x: Number(c.visual.x ?? 0),
                              y: Number(c.visual.y ?? 0),
                              width: Number(c.visual.width ?? 0),
                              height: Number(c.visual.height ?? 0),
                              alpha: Number(c.visual.alpha ?? 1),
                              crop: c.visual.crop
                                  ? {
                                        x: Number(c.visual.crop.x ?? 0),
                                        y: Number(c.visual.crop.y ?? 0),
                                        width: Number(c.visual.crop.width ?? 1),
                                        height: Number(c.visual.crop.height ?? 1),
                                    }
                                  : undefined,
                          }
                        : undefined,
                    layerIndex: Number.isFinite(c.layerIndex) ? Number(c.layerIndex) : idx,
                })).filter(s => s.assetId);
            } catch {}
        }

        return snap;
    }

    private static async applyInverse(entry: UndoEntry): Promise<void> {
        switch (entry.kind) {
            case "clip:update":
                await this.applySnapshotToExistingClip(entry.clipId, entry.before);
                return;
            case "clip:add":
                CompositionService.deleteClipById(entry.clip.id);
                return;
            case "clip:remove":
                await this.recreateClip(entry.clip);
                return;
            case "script:edits":
                await this.applyScriptEdits(entry.clipId, entry.before);
                return;
            case "canvas:resize":
                CompositionService.composition.resize(entry.before.width, entry.before.height);
                return;
            case "canvas:bg":
                CompositionService.composition.changeBackground(entry.before as any);
                return;
        }
    }

    private static async applyForward(entry: UndoEntry): Promise<void> {
        switch (entry.kind) {
            case "clip:update":
                await this.applySnapshotToExistingClip(entry.clipId, entry.after);
                return;
            case "clip:add":
                await this.recreateClip(entry.clip);
                return;
            case "clip:remove":
                CompositionService.deleteClipById(entry.clip.id);
                return;
            case "script:edits":
                await this.applyScriptEdits(entry.clipId, entry.after);
                return;
            case "canvas:resize":
                CompositionService.composition.resize(entry.after.width, entry.after.height);
                return;
            case "canvas:bg":
                CompositionService.composition.changeBackground(entry.after as any);
                return;
        }
    }

    private static async applySnapshotToExistingClip(clipId: string, snap: ClipSnapshot): Promise<void> {
        const clip: any = CompositionService.getClipById(clipId);
        if (!clip) return;

        // Timing
        try {
            if ("range" in clip && "offset" in clip) {
                const inSec = Number(snap.inSec ?? 0);
                const outSec = Number(snap.outSec ?? inSec);
                const offsetSec = Number(snap.offsetSec ?? 0);
                clip.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(outSec)];
                clip.offset = Timestamp.fromSeconds(offsetSec);
            }
        } catch {}

        // Props
        try {
            const pr = Number(snap.props?.playbackRate ?? 1);
            if (typeof clip.playbackRate === "number" || clip.playbackRate === undefined) {
                clip.playbackRate = Number.isFinite(pr) ? pr : 1;
            }
        } catch {}
        try {
            const g = Number(snap.props?.gain ?? 1);
            if (typeof clip.gain === "number" || clip.gain === undefined) {
                clip.gain = Number.isFinite(g) ? g : 1;
            }
        } catch {}
        try {
            const a = Number(snap.props?.alpha ?? 1);
            if ("alpha" in clip) clip.alpha = Number.isFinite(a) ? a : 1;
        } catch {}

        // Visual
        const v = snap.visual;
        if (v) {
            try {
                if (typeof (clip as any).setCrop === "function" && v.crop) {
                    (clip as any).setCrop(v.crop);
                }
            } catch {}
            try {
                if (typeof v.x === "number" && typeof v.y === "number") {
                    if ("position" in clip) clip.position = { x: v.x, y: v.y };
                    else {
                        if ("x" in clip) clip.x = v.x;
                        if ("y" in clip) clip.y = v.y;
                    }
                }
            } catch {}
            try { if (typeof v.width === "number") clip.width = v.width; } catch {}
            try { if (typeof v.height === "number") clip.height = v.height; } catch {}
            try { if (typeof v.rotation === "number" && "rotation" in clip) clip.rotation = v.rotation; } catch {}
            try { if (typeof v.scaleX === "number" && typeof v.scaleY === "number" && "scale" in clip) clip.scale = { x: v.scaleX, y: v.scaleY }; } catch {}
        }

        // Script edits
        if (snap.type === "script" && clip instanceof ScriptLayerClip) {
            try {
                (clip as any).applyPersistedEdits?.({
                    scriptCuts: Array.isArray(snap.scriptCuts) ? snap.scriptCuts : [],
                    gaps: Array.isArray(snap.gaps) ? snap.gaps : [],
                    gapInsertions: Array.isArray(snap.gapInsertions) ? snap.gapInsertions : [],
                    splits: Array.isArray(snap.splits) ? snap.splits : [],
                    speakerOverrides: Array.isArray(snap.speakerOverrides) ? snap.speakerOverrides : [],
                    speakerMuteRanges: Array.isArray(snap.speakerMuteRanges) ? snap.speakerMuteRanges : [],
                    transcriptHiddenRanges: Array.isArray(snap.transcriptHiddenRanges) ? snap.transcriptHiddenRanges : [],
                    wordCorrections: Array.isArray(snap.wordCorrections) ? snap.wordCorrections : [],
                    transcriptParagraphBreaks: Array.isArray(snap.transcriptParagraphBreaks) ? snap.transcriptParagraphBreaks : [],
                    transcriptMarkers: Array.isArray(snap.transcriptMarkers) ? snap.transcriptMarkers : [],
                });
            } catch {}

            // Restore per-source segment placement + config-level values (gain/playback/visual).
            // This is critical for undo/redo of operations like inserting empty scenes or copy/paste.
            try {
                const cfgs: any[] = typeof (clip as any).getInputConfigs === "function" ? (clip as any).getInputConfigs() : [];
                const next = Array.isArray(snap.sources) ? snap.sources : [];

                for (const src of next) {
                    const assetId = String((src as any)?.assetId ?? "").trim();
                    if (!assetId) continue;
                    const cfg = cfgs.find((c: any) => String(c?.assetId ?? "") === assetId);
                    if (!cfg) continue;

                    cfg.name = (src as any).name ?? cfg.name;
                    cfg.color = (src as any).color ?? cfg.color;
                    cfg.gain = Number.isFinite((src as any).gain) ? Number((src as any).gain) : cfg.gain;
                    cfg.playbackRate = Number.isFinite((src as any).playbackRate) ? Number((src as any).playbackRate) : cfg.playbackRate;
                    cfg.offsetSec = Number.isFinite((src as any).offsetSec) ? Number((src as any).offsetSec) : cfg.offsetSec;
                    cfg.range = (src as any).range
                        ? { inSec: Number((src as any).range.inSec ?? 0), outSec: Number((src as any).range.outSec ?? 0) }
                        : cfg.range;
                    cfg.visual = (src as any).visual ?? cfg.visual;
                    cfg.layerIndex = Number.isFinite((src as any).layerIndex) ? Number((src as any).layerIndex) : cfg.layerIndex;

                    const segsRaw: any[] = Array.isArray((src as any).segments) ? ((src as any).segments as any[]) : [];
                    if (segsRaw.length > 0) {
                        cfg.segments = segsRaw
                            .map((s: any) => {
                                const segId = s?.id ? String(s.id) : crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;
                                const offsetSec = Number(s?.offsetSec ?? NaN);
                                const rin = Number(s?.range?.inSec ?? NaN);
                                const rout = Number(s?.range?.outSec ?? NaN);
                                if (!Number.isFinite(offsetSec) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
                                const rec: any = { id: segId, offsetSec, range: { inSec: rin, outSec: rout } };
                                const pr = s?.playbackRate;
                                if (Number.isFinite(pr) && Number(pr) > 0) rec.playbackRate = Number(pr);
                                return rec;
                            })
                            .filter(Boolean)
                            .sort((a: any, b: any) => Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0));
                    } else {
                        // No segments stored in this snapshot; clear to fall back to legacy range/offset.
                        if (Array.isArray(cfg.segments)) cfg.segments = [];
                        else delete cfg.segments;
                    }

                    // Keep derived runtime entries in sync (segments are referenced by pointer in many places).
                    try {
                        for (const e of (clip as any).audioEntries ?? []) {
                            if (e?.input === cfg.input) (e as any).segments = cfg.segments;
                        }
                        for (const e of (clip as any).videoEntries ?? []) {
                            if (e?.input === cfg.input) (e as any).segments = cfg.segments;
                        }
                    } catch {}
                }
            } catch {}

            // Restore script clip extra state: multicam setup + sceneProperties
            try {
                (clip as any).multicamCameraSetup = snap.multicamCameraSetup ? JSON.parse(JSON.stringify(snap.multicamCameraSetup)) : null;
            } catch {}

            try {
                const sceneProps = Array.isArray(snap.sceneProperties) ? snap.sceneProperties : [];
                const nextMap = new Map<number, any>();
                for (const sp of sceneProps) {
                    const sceneIndex = Number((sp as any)?.sceneIndex);
                    if (!Number.isFinite(sceneIndex) || sceneIndex < 0) continue;
                    const props: any = {};
                    if (Number.isFinite((sp as any)?.gain)) props.gain = Number((sp as any).gain);
                    if (Number.isFinite((sp as any)?.playbackRate)) props.playbackRate = Number((sp as any).playbackRate);
                    if (typeof (sp as any)?.backgroundColor === "string" && String((sp as any).backgroundColor).trim()) {
                        props.backgroundColor = String((sp as any).backgroundColor).trim();
                    }
                    try {
                        const ag = (sp as any)?.audioSourceGains;
                        if (Array.isArray(ag) && ag.length > 0) {
                            const m = new Map<number, number>();
                            for (const entry of ag) {
                                const sourceIndex = Number((entry as any)?.sourceIndex);
                                const gain = Number((entry as any)?.gain);
                                if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                if (!Number.isFinite(gain)) continue;
                                m.set(sourceIndex, gain);
                            }
                            if (m.size > 0) props.audioSourceGains = m;
                        }
                    } catch {}
                    if (typeof (sp as any)?.layout === "string") props.layout = String((sp as any).layout);
                    if ((sp as any)?.layoutByAspect && typeof (sp as any).layoutByAspect === "object") {
                        const cleaned: any = {};
                        for (const key of ["horizontal", "vertical", "square"]) {
                            if (!Object.prototype.hasOwnProperty.call((sp as any).layoutByAspect, key)) continue;
                            const v = ((sp as any).layoutByAspect as any)[key];
                            if (v === null) cleaned[key] = null;
                            else if (typeof v === "string" && v) cleaned[key] = v;
                        }
                        if (Object.keys(cleaned).length > 0) props.layoutByAspect = cleaned;
                    }
                    if ((sp as any)?.inTransition) props.inTransition = JSON.parse(JSON.stringify((sp as any).inTransition));
                    if ((sp as any)?.outTransition) props.outTransition = JSON.parse(JSON.stringify((sp as any).outTransition));

                    const vo = (sp as any)?.visualOverrides;
                    if (Array.isArray(vo) && vo.length > 0) {
                        const m = new Map<number, any>();
                        for (const entry of vo) {
                            const sourceIndex = Number((entry as any)?.sourceIndex);
                            if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                            const v: any = {};
                            if (Number.isFinite((entry as any)?.alpha)) v.alpha = Number((entry as any).alpha);
                            if (Number.isFinite((entry as any)?.x)) v.x = Number((entry as any).x);
                            if (Number.isFinite((entry as any)?.y)) v.y = Number((entry as any).y);
                            if (Number.isFinite((entry as any)?.width)) v.width = Number((entry as any).width);
                            if (Number.isFinite((entry as any)?.height)) v.height = Number((entry as any).height);
                            try {
                                const crop = (entry as any)?.crop;
                                if (crop && typeof crop === "object") {
                                    const x = Number((crop as any).x);
                                    const y = Number((crop as any).y);
                                    const width = Number((crop as any).width);
                                    const height = Number((crop as any).height);
                                    if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                        v.crop = { x, y, width, height };
                                    }
                                }
                            } catch {}
                            if (typeof (entry as any)?.maskShape === "string") v.maskShape = String((entry as any).maskShape);
                            if (Number.isFinite((entry as any)?.rotation)) v.rotation = Number((entry as any).rotation);
                            if (Object.keys(v).length > 0) m.set(sourceIndex, v);
                        }
                        if (m.size > 0) props.visualOverrides = m;
                    }

                    const voba = (sp as any)?.visualOverridesByAspect;
                    if (voba && typeof voba === "object") {
                        const outByAspect: any = {};
                        for (const [presetKey, arrRaw] of Object.entries(voba)) {
                            const arr: any[] = Array.isArray(arrRaw) ? (arrRaw as any[]) : [];
                            if (!arr.length) continue;
                            const m = new Map<number, any>();
                            for (const entry of arr) {
                                const sourceIndex = Number((entry as any)?.sourceIndex);
                                if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                const v: any = {};
                                if (Number.isFinite((entry as any)?.alpha)) v.alpha = Number((entry as any).alpha);
                                if (Number.isFinite((entry as any)?.x)) v.x = Number((entry as any).x);
                                if (Number.isFinite((entry as any)?.y)) v.y = Number((entry as any).y);
                                if (Number.isFinite((entry as any)?.width)) v.width = Number((entry as any).width);
                                if (Number.isFinite((entry as any)?.height)) v.height = Number((entry as any).height);
                                try {
                                    const crop = (entry as any)?.crop;
                                    if (crop && typeof crop === "object") {
                                        const x = Number((crop as any).x);
                                        const y = Number((crop as any).y);
                                        const width = Number((crop as any).width);
                                        const height = Number((crop as any).height);
                                        if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                            v.crop = { x, y, width, height };
                                        }
                                    }
                                } catch {}
                                if (typeof (entry as any)?.maskShape === "string") v.maskShape = String((entry as any).maskShape);
                                if (Number.isFinite((entry as any)?.rotation)) v.rotation = Number((entry as any).rotation);
                                if (Object.keys(v).length > 0) m.set(sourceIndex, v);
                            }
                            if (m.size > 0) outByAspect[String(presetKey)] = m;
                        }
                        if (Object.keys(outByAspect).length > 0) props.visualOverridesByAspect = outByAspect;
                    }

                    if (Object.keys(props).length > 0) nextMap.set(sceneIndex, props);
                }

                (clip as any).sceneProperties = nextMap;
                (clip as any)._scenesCache = null;
            } catch {}

            // Broadcast script-layer state so scene bar/transcript stay consistent.
            try {
                const hasVideo = (clip.getVideoSources?.()?.length ?? 0) > 0;
                CompositionService.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                    clipId,
                    cuts: clip.scriptCuts ?? [],
                    gaps: clip.gaps ?? [],
                    gapInsertions: (clip as any).gapInsertions ?? [],
                    segments: clip.scriptSegments ?? [],
                    splits: clip.splits ?? [],
                    duration: clip.duration?.seconds ?? 0,
                    hasVideo,
                });
                (CompositionService.composition as any).trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                    script: {
                        layerId: CompositionService.composition.scriptLayer?.layerId ?? null,
                        clipId,
                        cutsChanged: true,
                        gapsChanged: true,
                        insertionsChanged: true,
                        splitsChanged: true,
                        speakerOverridesChanged: true,
                        speakerMutesChanged: true,
                        transcriptHiddenRangesChanged: true,
                        wordCorrectionsChanged: true,
                        transcriptParagraphBreaksChanged: true,
                        transcriptMarkersChanged: true,
                    },
                });
            } catch {}
        }

        try { clip.trigger?.("frame", undefined); } catch {}
        try { CompositionService.composition.computeFrame(); } catch {}
        // Notify store/persistence subscribers (visual changes don't emit on their own).
        try {
            (CompositionService.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip });
            (CompositionService.composition as any)?.trigger?.(EngineInternalEventsEnum.CLIP_UPDATED, { clip });
        } catch {}

        // Update shadow baseline.
        const next = this.snapshotClip(clip);
        this.clipShadow.set(next.id, next);
        if (next.type === "script") {
            this.scriptShadow.set(next.id, {
                scriptCuts: next.scriptCuts ?? [],
                gaps: next.gaps ?? [],
                gapInsertions: next.gapInsertions ?? [],
                splits: next.splits ?? [],
                speakerOverrides: next.speakerOverrides ?? [],
                speakerMuteRanges: next.speakerMuteRanges ?? [],
                transcriptHiddenRanges: next.transcriptHiddenRanges ?? [],
                wordCorrections: next.wordCorrections ?? [],
                transcriptParagraphBreaks: next.transcriptParagraphBreaks ?? [],
                transcriptMarkers: next.transcriptMarkers ?? [],
            });
        }
    }

    private static async applyScriptEdits(
        clipId: string,
        edits: Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">
    ): Promise<void> {
        const clip: any = CompositionService.getClipById(clipId);
        if (!clip || String(clip.type) !== "script") return;
        try {
            clip.applyPersistedEdits?.({
                scriptCuts: Array.isArray(edits.scriptCuts) ? edits.scriptCuts : [],
                gaps: Array.isArray(edits.gaps) ? edits.gaps : [],
                gapInsertions: Array.isArray(edits.gapInsertions) ? edits.gapInsertions : [],
                splits: Array.isArray(edits.splits) ? edits.splits : [],
                speakerOverrides: Array.isArray(edits.speakerOverrides) ? edits.speakerOverrides : [],
                speakerMuteRanges: Array.isArray(edits.speakerMuteRanges) ? edits.speakerMuteRanges : [],
                transcriptHiddenRanges: Array.isArray(edits.transcriptHiddenRanges) ? edits.transcriptHiddenRanges : [],
                wordCorrections: Array.isArray(edits.wordCorrections) ? edits.wordCorrections : [],
                transcriptParagraphBreaks: Array.isArray(edits.transcriptParagraphBreaks) ? edits.transcriptParagraphBreaks : [],
                transcriptMarkers: Array.isArray(edits.transcriptMarkers) ? edits.transcriptMarkers : [],
            });
        } catch {}

        // Broadcast state so UI + persistence sync.
        try {
            const hasVideo = (clip.getVideoSources?.()?.length ?? 0) > 0;
            CompositionService.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                clipId,
                cuts: clip.scriptCuts ?? [],
                gaps: clip.gaps ?? [],
                gapInsertions: clip.gapInsertions ?? [],
                segments: clip.scriptSegments ?? [],
                splits: clip.splits ?? [],
                duration: clip.duration?.seconds ?? 0,
                hasVideo,
            });
            (CompositionService.composition as any).trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: CompositionService.composition.scriptLayer?.layerId ?? null,
                    clipId,
                    cutsChanged: true,
                    gapsChanged: true,
                    insertionsChanged: true,
                    speakerOverridesChanged: true,
                    speakerMutesChanged: true,
                    transcriptHiddenRangesChanged: true,
                    wordCorrectionsChanged: true,
                    transcriptParagraphBreaksChanged: true,
                    transcriptMarkersChanged: true,
                },
            });
        } catch {}

        this.scriptShadow.set(clipId, {
            scriptCuts: Array.isArray(clip.scriptCuts) ? [...clip.scriptCuts] : [],
            gaps: Array.isArray(clip.gaps) ? [...clip.gaps] : [],
            gapInsertions: Array.isArray(clip.gapInsertions) ? [...clip.gapInsertions] : [],
            splits: Array.isArray(clip.splits) ? [...clip.splits] : [],
            speakerOverrides: Array.isArray(clip.speakerOverrides) ? [...clip.speakerOverrides] : [],
            speakerMuteRanges: Array.isArray((clip as any).speakerMuteRanges) ? [...(clip as any).speakerMuteRanges] : [],
            transcriptHiddenRanges: Array.isArray((clip as any).transcriptHiddenRanges) ? [...(clip as any).transcriptHiddenRanges] : [],
            wordCorrections: Array.isArray((clip as any).wordCorrections) ? [...(clip as any).wordCorrections] : [],
            transcriptParagraphBreaks: Array.isArray((clip as any).transcriptParagraphBreaks) ? [...(clip as any).transcriptParagraphBreaks] : [],
            transcriptMarkers: Array.isArray((clip as any).transcriptMarkers) ? [...(clip as any).transcriptMarkers] : [],
        });
    }

    private static async recreateClip(snap: ClipSnapshot): Promise<void> {
        if (!this.resolveAssetUrl) {
            console.warn("[UndoManager] Cannot recreate clip: resolveAssetUrl not set");
            return;
        }
        if (!CompositionService.isInitialized()) return;
        const comp: any = CompositionService.composition;

        // Avoid duplicates.
        if (snap?.id && CompositionService.getClipById(snap.id)) return;

        const layerId = snap.layerId || "";
        const type = String(snap.type ?? "").toLowerCase();

        let clip: any | null = null;

        if (type === "script") {
            const cfgs = Array.isArray(snap.sources) ? snap.sources : [];
            if (!cfgs.length) return;
            const inputConfigs: ScriptLayerInputConfig[] = [];
            for (const src of cfgs) {
                const assetId = String(src.assetId ?? "");
                if (!assetId) continue;
                const url = await this.resolveAssetUrl(assetId, "video");
                if (!url) continue;
                inputConfigs.push({
                    segments: Array.isArray((src as any).segments)
                        ? ((src as any).segments as any[])
                              .map((s: any) => {
                                  const segId = s?.id ? String(s.id) : crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;
                                  const offsetSec = Number(s?.offsetSec ?? NaN);
                                  const rin = Number(s?.range?.inSec ?? NaN);
                                  const rout = Number(s?.range?.outSec ?? NaN);
                                  if (!Number.isFinite(offsetSec) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
                                  const rec: any = { id: segId, offsetSec, range: { inSec: rin, outSec: rout } };
                                  const pr = s?.playbackRate;
                                  if (Number.isFinite(pr) && Number(pr) > 0) rec.playbackRate = Number(pr);
                                  return rec;
                              })
                              .filter(Boolean)
                        : undefined,
                    input: new Input({ source: new UrlSource(url), formats: ALL_FORMATS }),
                    assetId,
                    name: src.name ?? "",
                    color: src.color ?? undefined,
                    gain: Number.isFinite(src.gain) ? Number(src.gain) : 1.0,
                    playbackRate: Number.isFinite(src.playbackRate) ? Number(src.playbackRate) : 1.0,
                    offsetSec: Number.isFinite(src.offsetSec) ? Number(src.offsetSec) : 0,
                    range: src.range ? { inSec: Number(src.range.inSec ?? 0), outSec: Number(src.range.outSec ?? 0) } : undefined,
                    visual: src.visual
                        ? {
                              x: Number(src.visual.x ?? 0),
                              y: Number(src.visual.y ?? 0),
                              width: Number(src.visual.width ?? 0),
                              height: Number(src.visual.height ?? 0),
                              alpha: Number(src.visual.alpha ?? 1),
                              crop: src.visual.crop
                                  ? {
                                        x: Number(src.visual.crop.x ?? 0),
                                        y: Number(src.visual.crop.y ?? 0),
                                        width: Number(src.visual.crop.width ?? 1),
                                        height: Number(src.visual.crop.height ?? 1),
                                    }
                                  : undefined,
                          }
                        : undefined,
                    layerIndex: Number.isFinite(src.layerIndex) ? Number(src.layerIndex) : undefined,
                });
            }
            if (!inputConfigs.length) return;
            clip = ScriptLayerClip.fromInputConfigs(
                inputConfigs,
                { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width, height: comp.height } as any,
                snap.id
            );

            // Apply edits after timing is restored.
            try {
                (clip as any).applyPersistedEdits?.({
                    scriptCuts: Array.isArray(snap.scriptCuts) ? snap.scriptCuts : [],
                    gaps: Array.isArray(snap.gaps) ? snap.gaps : [],
                    splits: Array.isArray(snap.splits) ? snap.splits : [],
                    speakerOverrides: Array.isArray(snap.speakerOverrides) ? snap.speakerOverrides : [],
                    speakerMuteRanges: Array.isArray(snap.speakerMuteRanges) ? snap.speakerMuteRanges : [],
                    transcriptHiddenRanges: Array.isArray(snap.transcriptHiddenRanges) ? snap.transcriptHiddenRanges : [],
                    wordCorrections: Array.isArray(snap.wordCorrections) ? snap.wordCorrections : [],
                    transcriptParagraphBreaks: Array.isArray(snap.transcriptParagraphBreaks) ? snap.transcriptParagraphBreaks : [],
                });
            } catch {}

            // Insert into script layer.
            // @ts-ignore
            await comp.insertClipIntoScriptLayer(clip, layerId || undefined);
        } else if (type === "image") {
            const assetId = String(snap.assetId ?? "");
            if (!assetId) return;
            const url = await this.resolveAssetUrl(assetId, "image");
            if (!url) return;
            const ref: MediaSourceRef = { id: assetId, kind: "image", imgUrl: url } as any;
            clip = new ImageClip(
                ref,
                { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width, height: comp.height } as any,
                snap.id
            );
        } else if (type === "video" || type === "audio") {
            const assetId = String(snap.assetId ?? "");
            if (!assetId) return;
            const kind = type === "video" ? "video" : "audio";
            const url = await this.resolveAssetUrl(assetId, kind);
            if (!url) return;
            const source = new UrlSource(url);
            const input = new Input({ source, formats: ALL_FORMATS });
            const ref: MediaSourceRef = { id: assetId, kind, input } as any;
            clip =
                type === "video"
                    ? new VideoClip(
                          ref,
                          { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width, height: comp.height } as any,
                          snap.id
                      )
                    : new AudioClip(ref, {}, snap.id);
        } else {
            return;
        }

        if (!clip) return;
        try { clip.name = snap.name ?? ""; } catch {}

        // Add to composition first so applySnapshot can find the live instance.
        if (type !== "script") {
            await comp.add(clip, layerId || undefined);
        }

        // Apply snapshot properties to match original.
        await this.applySnapshotToExistingClip(clip.id, snap);

        // Ensure asset mapping is restored.
        try { if (snap.assetId) CompositionStateStore.setAssetIdForClip(clip.id, snap.assetId); } catch {}
        try { if (snap.thumbnailId) CompositionStateStore.setThumbnailIdForClip(clip.id, snap.thumbnailId); } catch {}
        try { if (snap.name) CompositionStateStore.setTitleForClip(clip.id, snap.name); } catch {}
    }
}
